import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from '@headlessui/react';
import { Bars3Icon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Link, useLocation } from 'react-router-dom';
import { match } from '@blind-kings/game-core';
import { useAuthContext } from '../contexts/AuthContext';
import { usePlayModalContext } from '../contexts/PlayModalContext';
import { useSnackBarContext } from '../contexts/SnackBarContext';
import { resolveOnlineGameId } from '../utils/active-games';
import { formatCurrencyAmount } from '../utils/format-currency';
import Coin from './Coin';
import Gem from './Gem';
import NavProfileChip from './NavProfileChip';
import BkButton from './ui/BkButton';

const primaryNav = [
  { to: '/pass', name: 'Pass', emphasize: false },
  { to: '/leaderboard', name: 'Ranks', emphasize: false },
  { to: '/store', name: 'Store', emphasize: false },
  { to: '/rules', name: 'Rules', emphasize: false },
  { to: '/community', name: 'Community', emphasize: false },
];

function boostLabel(days: number) {
  if (days < 1) {
    return days * 24 < 1
      ? 'Less than 1 hour remaining'
      : `${Math.ceil(days * 24)} hours remaining`;
  }
  return `${Math.ceil(days)} days remaining`;
}

export default function AppNavBar() {
  const { pathname } = useLocation();
  const { openLoginModal, currentUser, userData, signOut } = useAuthContext();
  const { openPlayModal } = usePlayModalContext();

  const currentRoute = match(pathname)
    .regex(/^\/$/, 'Play')
    .regex(/\/play/, 'Play')
    .regex(/\/queue/, 'Play')
    .regex(/\/pass/, 'Pass')
    .regex(/\/leaderboard/, 'Ranks')
    .regex(/\/store/, 'Store')
    .regex(/\/community/, 'Community')
    .regex(/\/rules/, 'Rules')
    .regex(/\/user/, 'User')
    .otherwise('Home') as string;

  const {
    displayName,
    coins = 0,
    gems = 0,
    level = 1,
    xpBoostDays = 0,
    coinBoostDays = 0,
    avatarId,
  } = userData ?? {};

  const inMatch = Boolean(resolveOnlineGameId(userData));
  const { addSnackBar } = useSnackBarContext();
  const profileBase = userData?.displayName
    ? `/user/${userData.displayName}`
    : '/';

  const userMenu: Array<{ name: string; to?: string; onClick?: () => void }> = [
    { name: 'Profile', to: `${profileBase}?tab=overview` },
    { name: 'Match History', to: `${profileBase}?tab=history` },
    { name: 'Skins', to: `${profileBase}?tab=skins` },
    { name: 'Emotes', to: `${profileBase}?tab=emotes` },
    { name: 'Preferences', to: `${profileBase}?tab=preferences` },
    {
      name: 'Sign Out',
      onClick: () => {
        void signOut();
      },
    },
  ];

  const linkClass = (name: string, emphasize?: boolean) => {
    const active = currentRoute === name;
    if (emphasize) {
      return [
        'cursor-pointer px-4 py-1.5 font-bold uppercase tracking-wider text-sm border transition-colors duration-200',
        active
          ? 'border-bk-gold bg-bk-gold/15 text-bk-gold-bright'
          : 'border-bk-gold-dim text-bk-gold hover:border-bk-gold hover:text-bk-gold-bright hover:bg-bk-gold/10',
      ].join(' ');
    }
    return [
      'cursor-pointer px-3 py-2 font-medium uppercase tracking-wide text-sm transition-colors duration-200',
      active ? 'text-bk-gold-bright' : 'text-bk-muted hover:text-bk-gold',
    ].join(' ');
  };

  return (
    <Disclosure
      as="nav"
      className="relative z-40 shrink-0 border-b border-bk-gold-dim/40 bg-bk-bg-mid/95 backdrop-blur-md"
    >
      {({ open }) => (
        <>
          <div className="relative flex h-16 items-center justify-between px-2 sm:px-6 lg:px-10">
            <div className="flex items-center gap-2 sm:gap-6">
              <div className="flex items-center sm:hidden">
                <DisclosureButton className="inline-flex items-center justify-center rounded-md p-2 text-bk-muted hover:text-bk-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>

              <Link
                to="/"
                className="group flex items-center gap-2 shrink-0"
                aria-current={currentRoute === 'Home' ? 'page' : undefined}
              >
                <span className="font-extrabold text-lg sm:text-xl tracking-tight text-bk-text group-hover:text-bk-gold-bright transition-colors">
                  Blind<span className="text-bk-gold">Kings</span>
                </span>
              </Link>

              <div className="hidden sm:flex sm:items-center sm:gap-1">
                <button
                  type="button"
                  className={linkClass('Play', true)}
                  onClick={openPlayModal}
                  aria-current={currentRoute === 'Play' ? 'page' : undefined}
                >
                  Play
                </button>
                {primaryNav.map((item) => (
                  <Link
                    key={item.name}
                    className={linkClass(item.name, item.emphasize)}
                    to={item.to}
                    aria-current={currentRoute === item.name ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {currentUser ? (
              userData ? (
                <div className="flex items-center gap-2 sm:gap-4">
                  <Link
                    to="/store"
                    className="group flex flex-col justify-center gap-1 py-1 pr-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold"
                    title="Open store"
                    aria-label={`${formatCurrencyAmount(coins)} coins, ${formatCurrencyAmount(gems)} gems. Open store`}
                  >
                    <span className="flex items-center gap-1.5 leading-none">
                      <Coin />
                      <span className="text-xs font-semibold tabular-nums text-bk-gold transition-colors group-hover:text-bk-gold-bright sm:text-sm">
                        {formatCurrencyAmount(coins)}
                      </span>
                    </span>
                    <span className="flex items-center gap-1.5 leading-none">
                      <Gem />
                      <span className="text-xs font-semibold tabular-nums text-fuchsia-300 transition-colors group-hover:text-fuchsia-200 sm:text-sm">
                        {formatCurrencyAmount(gems)}
                      </span>
                    </span>
                  </Link>

                  <NavProfileChip
                    displayName={String(displayName || 'Player')}
                    level={Number(level) || 1}
                    avatarId={
                      typeof avatarId === 'string' ? avatarId : undefined
                    }
                    inMatch={inMatch}
                    menu={
                      <Menu as="div" className="relative">
                        <MenuButton className="flex items-center self-stretch px-0.5 text-bk-muted transition-colors hover:text-bk-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold">
                          <span className="sr-only">Open account menu</span>
                          <ChevronDownIcon width={16} height={16} />
                        </MenuButton>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-200"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <MenuItems className="absolute right-0 z-50 mt-2 w-52 origin-top-right border border-bk-gold-dim/50 bg-bk-panel py-1 shadow-xl focus:outline-none">
                            <div className="border-b border-bk-border px-4 py-2 sm:hidden">
                              <div className="flex items-center gap-2 text-sm text-bk-muted">
                                <span className="text-bk-gold font-bold">Lv {level}</span>
                                <Coin />
                                <span className="text-bk-gold tabular-nums">
                                  {formatCurrencyAmount(coins)}
                                </span>
                                <Gem />
                                <span className="text-fuchsia-300 tabular-nums">
                                  {formatCurrencyAmount(gems)}
                                </span>
                              </div>
                            </div>
                            {xpBoostDays > 0 ? (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    className={`block w-full px-4 py-2 text-left text-sm cursor-pointer ${focus ? 'bg-bk-panel-raised text-bk-gold' : 'text-bk-text'}`}
                                    onClick={() => {
                                      addSnackBar({
                                        title: 'Double XP',
                                        message: `You have ${boostLabel(xpBoostDays)} for your double xp boost`,
                                        variant: 'info',
                                        timeout: 2500,
                                      });
                                    }}
                                  >
                                    x2 XP — {boostLabel(xpBoostDays)}
                                  </button>
                                )}
                              </MenuItem>
                            ) : null}
                            {coinBoostDays > 0 ? (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    className={`block w-full px-4 py-2 text-left text-sm cursor-pointer ${focus ? 'bg-bk-panel-raised text-bk-gold' : 'text-bk-text'}`}
                                    onClick={() => {
                                      addSnackBar({
                                        title: 'Double Coins',
                                        message: `You have ${boostLabel(coinBoostDays)} for your double coin boost`,
                                        variant: 'info',
                                        timeout: 2500,
                                      });
                                    }}
                                  >
                                    x2 Coins — {boostLabel(coinBoostDays)}
                                  </button>
                                )}
                              </MenuItem>
                            ) : null}
                            {userMenu.map(({ name, onClick, to }) => (
                              <MenuItem key={name}>
                                {({ focus }) =>
                                  onClick ? (
                                    <button
                                      type="button"
                                      className={`block w-full px-4 py-2 text-left text-sm text-bk-text cursor-pointer ${focus ? 'bg-bk-panel-raised text-bk-gold' : ''}`}
                                      onClick={onClick}
                                    >
                                      {name}
                                    </button>
                                  ) : (
                                    <Link
                                      className={`block px-4 py-2 text-sm text-bk-text ${focus ? 'bg-bk-panel-raised text-bk-gold' : ''}`}
                                      to={to ?? '#'}
                                    >
                                      {name}
                                    </Link>
                                  )
                                }
                              </MenuItem>
                            ))}
                          </MenuItems>
                        </Transition>
                      </Menu>
                    }
                  />
                </div>
              ) : (
                <AiOutlineLoading3Quarters className="animate-spin text-bk-gold" />
              )
            ) : (
              <BkButton variant="primary" size="sm" onClick={openLoginModal}>
                Login
              </BkButton>
            )}
          </div>

          <DisclosurePanel className="border-t border-bk-border sm:hidden">
            <div className="space-y-1 px-2 py-3">
              <DisclosureButton
                as="button"
                type="button"
                onClick={openPlayModal}
                className={`block w-full rounded-sm px-3 py-2 text-left text-base font-medium uppercase tracking-wide cursor-pointer ${
                  currentRoute === 'Play'
                    ? 'text-bk-gold-bright bg-bk-gold/10'
                    : 'text-bk-muted hover:text-bk-gold'
                }`}
                aria-current={currentRoute === 'Play' ? 'page' : undefined}
              >
                Play
              </DisclosureButton>
              {primaryNav.map((item) => (
                <DisclosureButton
                  key={item.name}
                  as={Link}
                  to={item.to}
                  className={`block rounded-sm px-3 py-2 text-base font-medium uppercase tracking-wide ${
                    currentRoute === item.name
                      ? 'text-bk-gold-bright bg-bk-gold/10'
                      : 'text-bk-muted hover:text-bk-gold'
                  }`}
                  aria-current={currentRoute === item.name ? 'page' : undefined}
                >
                  {item.name}
                </DisclosureButton>
              ))}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
